import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { timeAgo, fmtDateTime } from "@/lib/format";
import { API } from "@/lib/api";
import { toast } from "sonner";
import { BookOpen, ChevronLeft, Download, MessageCircleQuestion, NotebookPen, Pencil, Plus, Printer, Search, Trash2, X } from "lucide-react";
import Seo from "@/components/Seo";
import EntryComposer from "@/components/journal/EntryComposer";
import JournalContent from "@/components/journal/JournalContent";

function EntryRow({ e, active, onOpen }) {
  return (
    <button type="button" onClick={onOpen} data-testid={`journal-entry-${e.id}`}
      className={`w-full rounded-xl border p-4 text-left transition-colors ${active ? "border-gold bg-gold/10" : "border-border bg-card hover:border-gold/60"}`}>
      <div className="flex items-center justify-between gap-2">
        <p className="truncate font-serif text-lg text-foreground">{e.title || (e.question ? e.question.reference : "Untitled entry")}</p>
        <span className="shrink-0 font-mono text-[10px] text-muted-foreground" title={fmtDateTime(e.created_at)}>{fmtDateTime(e.created_at, { weekday: undefined, year: undefined })}</span>
      </div>
      {e.question && <p className="no-reftag mt-1 truncate font-serif text-sm italic text-muted-foreground">“{e.question.question_kjv}”</p>}
      {e.excerpt && <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{e.excerpt}</p>}
      {e.mentions?.length > 0 && (
        <p className="mt-2 flex flex-wrap gap-1">
          {e.mentions.slice(0, 4).map((m, i) => (
            <span key={`${m.kind}-${m.label}-${i}`} className={`no-reftag inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 font-mono text-[10px] ${m.kind === "question" ? "bg-primary/10 text-primary" : "bg-gold/15 text-gold"}`}>
              {m.kind === "question" ? <MessageCircleQuestion className="h-3 w-3" /> : <BookOpen className="h-3 w-3" />}{m.label}
            </span>
          ))}
        </p>
      )}
    </button>
  );
}

function EntryView({ entry, onEdit, onDelete }) {
  return (
    <article className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="journal-entry-view">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          {entry.question && (
            <Link to={`/question/${entry.question.id}`} className="no-reftag inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-gold hover:underline" data-testid="entry-question-link">
              <MessageCircleQuestion className="h-3.5 w-3.5" /> {entry.question.reference}
            </Link>
          )}
          <h2 className="mt-1 font-serif text-3xl font-semibold text-foreground">{entry.title || (entry.question ? `On ${entry.question.reference}` : "Untitled entry")}</h2>
          <p className="mt-1 font-mono text-[11px] text-muted-foreground" data-testid="entry-timestamp">{fmtDateTime(entry.created_at)}{entry.updated_at !== entry.created_at ? ` · edited ${timeAgo(entry.updated_at)}` : ""}</p>
        </div>
        <div className="flex gap-1">
          <Button variant="outline" size="sm" className="rounded-full" onClick={onEdit} data-testid="entry-edit-button"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
          <Button variant="ghost" size="sm" className="rounded-full text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={onDelete} data-testid="entry-delete-button"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>
      {entry.question && <p className="mt-4 border-l-2 border-gold/50 pl-4 font-serif text-xl italic leading-tight text-foreground/90">“{entry.question.question_kjv}”</p>}
      <JournalContent doc={entry.doc} className="mt-6 text-[17px]" />
    </article>
  );
}

export default function Journal() {
  const { user, loading, openAuth } = useAuth();
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const qc = useQueryClient();
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(false);
  const isNew = id === "new";
  const questionFilter = params.get("question");

  const { data: list } = useQuery({
    queryKey: ["journal", search, questionFilter],
    queryFn: async () => (await api.get("/journal", { params: { search: search || undefined, question_id: questionFilter || undefined } })).data,
    enabled: !!user,
  });
  const { data: entry } = useQuery({
    queryKey: ["journal-entry", id],
    queryFn: async () => (await api.get(`/journal/${id}`)).data,
    enabled: !!user && !!id && !isNew,
  });
  useEffect(() => { setEditing(false); }, [id]);

  const refresh = () => { qc.invalidateQueries({ queryKey: ["journal"] }); qc.invalidateQueries({ queryKey: ["journal-entry"] }); };
  const remove = async () => {
    if (!window.confirm("Delete this entry? This cannot be undone.")) return;
    await api.delete(`/journal/${entry.id}`);
    toast.success("Entry deleted.");
    refresh();
    navigate("/journal");
  };

  if (loading) return <div className="mx-auto max-w-6xl px-4 py-20"><div className="h-40 animate-pulse rounded-2xl bg-muted" /></div>;
  if (!user) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="journal-gate">
        <NotebookPen className="mx-auto h-10 w-10 text-gold" />
        <h1 className="mt-4 font-serif text-4xl font-bold text-foreground sm:text-5xl">Your private journal</h1>
        <p className="mx-auto mt-4 max-w-md text-muted-foreground">Keep your prayers, reflections, and notes in one place. Type @ to link any scripture or question as you write.</p>
        <Button data-testid="journal-signin-button" onClick={openAuth} className="mt-8 rounded-full bg-primary px-8 py-6 text-base text-primary-foreground">Sign in to start writing</Button>
      </div>
    );
  }

  const entries = list?.entries || [];
  const showPane = isNew || !!id;

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8" data-testid="journal-page">
      <Seo title="Journal" description="Your private journal of prayers, reflections, and notes." />
      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <span className="font-mono text-xs uppercase tracking-[0.25em] text-gold">My study</span>
          <h1 className="mt-1 font-serif text-4xl font-bold tracking-tight text-foreground sm:text-5xl">Journal</h1>
          <p className="mt-2 text-sm text-muted-foreground">{list ? `${list.total} entr${list.total === 1 ? "y" : "ies"}` : " "} · Private to you</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {entries.length > 0 && (
            <>
              <Button asChild variant="outline" className="rounded-full" data-testid="journal-export-pdf">
                <a href={`${API}/journal/export.pdf`} download>
                  <Download className="mr-1 h-4 w-4" /> Download PDF
                </a>
              </Button>
              <Button variant="outline" className="rounded-full" onClick={() => window.open(`${API}/journal/export.pdf`, "_blank", "noopener")} data-testid="journal-print">
                <Printer className="mr-1 h-4 w-4" /> Print
              </Button>
            </>
          )}
          <Button onClick={() => navigate("/journal/new")} className="rounded-full bg-primary text-primary-foreground" data-testid="journal-new-button"><Plus className="mr-1 h-4 w-4" /> New entry</Button>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-[340px_1fr]">
        <aside className={`${showPane ? "hidden lg:block" : ""} space-y-3`} data-testid="journal-list">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search your journal" className="pl-9" data-testid="journal-search" />
          </div>
          {questionFilter && (
            <Link to="/journal" className="inline-flex items-center gap-1 rounded-full bg-gold/15 px-3 py-1 font-mono text-[11px] text-gold" data-testid="journal-filter-chip">
              Filtered to one question <X className="h-3 w-3" />
            </Link>
          )}
          {list && entries.length === 0 && (
            <div className="rounded-xl border border-dashed border-border p-8 text-center" data-testid="journal-empty">
              <p className="font-serif text-xl text-foreground">{search ? "Nothing matches that search." : "Your journal is empty."}</p>
              {!search && <p className="mt-1 text-sm text-muted-foreground">Start with today's Daily Spark, or write freely.</p>}
            </div>
          )}
          <div className="space-y-2 lg:max-h-[70vh] lg:overflow-y-auto lg:pr-1">
            {entries.map((e) => <EntryRow key={e.id} e={e} active={e.id === id} onOpen={() => navigate(`/journal/${e.id}`)} />)}
          </div>
        </aside>

        <section className={showPane ? "" : "hidden lg:block"}>
          {showPane && (
            <button type="button" onClick={() => navigate("/journal")} className="mb-3 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground lg:hidden" data-testid="journal-back">
              <ChevronLeft className="h-4 w-4" /> All entries
            </button>
          )}
          {isNew && (
            <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="journal-new-pane">
              <p className="font-mono text-[11px] uppercase tracking-wider text-gold">New entry</p>
              <div className="mt-4">
                <EntryComposer questionId={params.get("question")} seed={location.state?.seed} autoFocus onSaved={(e) => { refresh(); navigate(`/journal/${e.id}`); }} onCancel={() => navigate("/journal")} />
              </div>
            </div>
          )}
          {!isNew && id && entry && (editing ? (
            <div className="rounded-2xl border border-border bg-card p-6 sm:p-8" data-testid="journal-edit-pane">
              <p className="font-mono text-[11px] uppercase tracking-wider text-gold">Editing</p>
              <div className="mt-4"><EntryComposer entry={entry} autoFocus onSaved={() => { refresh(); setEditing(false); }} onCancel={() => setEditing(false)} /></div>
            </div>
          ) : (
            <EntryView entry={entry} onEdit={() => setEditing(true)} onDelete={remove} />
          ))}
          {!showPane && (
            <div className="flex h-full min-h-[320px] flex-col items-center justify-center rounded-2xl border border-dashed border-border p-10 text-center" data-testid="journal-placeholder">
              <NotebookPen className="h-10 w-10 text-muted-foreground" />
              <p className="mt-4 font-serif text-2xl text-foreground">Pick an entry, or start a new one.</p>
              <p className="mt-1 max-w-sm text-sm text-muted-foreground">Type <span className="font-mono text-gold">@matthew26:62</span> while writing to link a verse, or a few words to link a question.</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
